import type { LogEntry, LoggerOptions } from '@tbd/lift-logger';
import { formatters, Logger } from '@tbd/lift-logger';
import {
  CLOUDFLARE_ACCOUNT_NAME,
  CLOUDFLARE_PAGES_APEX,
  CLOUDFLARE_WORKERS_APEX,
  LIFT_APEXES,
  PORTAL_DEV_APEX,
  PORTAL_PREVIEW_APEX,
  PORTAL_PROD_APEX,
  PORTAL_STAGING_APEX,
} from './app-domains.js';

export { MONOREPO_VERSION as TBD_VERSION } from './generated-version.js';
export * from './app-domains.js';
export * from './bulk-import-status.js';
export * from './cookies.js';
export * from './remix-client/index.js';
// Type definitions
export type DeploymentEnvironment =
  'local' | 'dev' | 'preview' | 'staging' | 'production';

// #region Application constants
export const LIFT_PARTICIPANT_EMAIL_DOMAIN = 'lift-students.email';
// #endregion Application constants

export const APP_NAMES = {
  PORTAL: 'portal',
  ASSESSMENTS: 'assessments',
  PRACTICE: 'practice',
} as const;

export type AppName = (typeof APP_NAMES)[keyof typeof APP_NAMES];

export const CLOUDFLARE_PROJECT_NAMES = {
  [APP_NAMES.PORTAL]: 'lift-portal',
  [APP_NAMES.ASSESSMENTS]: 'lift-assessments',
  [APP_NAMES.PRACTICE]: 'lift-practice-vue',
} as const;

export type ProjectName =
  (typeof CLOUDFLARE_PROJECT_NAMES)[keyof typeof CLOUDFLARE_PROJECT_NAMES];

const WORKERS_PROJECTS = [
  CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PORTAL],
  CLOUDFLARE_PROJECT_NAMES[APP_NAMES.ASSESSMENTS],
] as const;
type WorkersProjectName = (typeof WORKERS_PROJECTS)[number];

export function isWorkersProjectName(
  projectName: ProjectName
): projectName is WorkersProjectName {
  return WORKERS_PROJECTS.includes(projectName as WorkersProjectName);
}

const PAGES_PROJECTS = [CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PRACTICE]] as const;
type PagesProjectName = (typeof PAGES_PROJECTS)[number];

export function isPagesProjectName(
  projectName: ProjectName
): projectName is PagesProjectName {
  return PAGES_PROJECTS.includes(projectName as PagesProjectName);
}

export function projectNameToAppName(projectName: string): AppName | undefined {
  return Object.entries(CLOUDFLARE_PROJECT_NAMES).find(
    ([, name]) => name === projectName
  )?.[0] as AppName | undefined;
}
export const APP_ORIGIN_HOSTNAMES = {
  [APP_NAMES.PORTAL]: `${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PORTAL]}.${CLOUDFLARE_ACCOUNT_NAME}.${CLOUDFLARE_WORKERS_APEX}`,
  [APP_NAMES.ASSESSMENTS]: `${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.ASSESSMENTS]}.${CLOUDFLARE_ACCOUNT_NAME}.${CLOUDFLARE_WORKERS_APEX}`,
  [APP_NAMES.PRACTICE]: `${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PRACTICE]}.${CLOUDFLARE_PAGES_APEX}`,
} as const;

const APP_NAME_LAST_PREVIEW_SUBDOMAIN_REGEX = new RegExp(
  `(?<prefix>.+)-(?<appname>${APP_NAMES.PORTAL}|${APP_NAMES.ASSESSMENTS}|${APP_NAMES.PRACTICE})$`
);

const PROJECT_NAME_FIRST_PREVIEW_SUBDOMAIN_REGEX = new RegExp(
  `^(?<appname>${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PORTAL]}|${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.ASSESSMENTS]}|${CLOUDFLARE_PROJECT_NAMES[APP_NAMES.PRACTICE]})-(?<prefix>.+)`
);

// eventually this will be imported from an internationalization / localization file
export const AppTitles = {
  [APP_NAMES.PORTAL]: 'LIFT Portal',
  [APP_NAMES.ASSESSMENTS]: 'LIFT Assessments',
  [APP_NAMES.PRACTICE]: 'LIFT Practice',
} as const;
export type AppTitle = (typeof AppTitles)[keyof typeof AppTitles];

type StackKey = AppName | 'otel_collector_origin';
type OriginStack = Record<StackKey, string>;
const DEV_OTEL_COLLECTOR = 'https://otel-collector-dev.laerdal-apps-dev.com';
const PROD_OTEL_COLLECTOR =
  'https://otel-collector-production.eu-central-1.laerdal-apps.com';
const ORIGIN_STACKS: Record<DeploymentEnvironment, OriginStack> = {
  local: {
    portal: 'http://localhost:5173',
    assessments: 'http://localhost:5174',
    practice: 'http://localhost:5175',
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  dev: {
    portal: `https://${PORTAL_DEV_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_DEV_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_DEV_APEX}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  preview: {
    portal: `https://${PORTAL_PREVIEW_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_PREVIEW_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_PREVIEW_APEX}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  staging: {
    portal: `https://${PORTAL_STAGING_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_STAGING_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_STAGING_APEX}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  },
  production: {
    portal: `https://${PORTAL_PROD_APEX}`,
    assessments: `https://${APP_NAMES.ASSESSMENTS}.${PORTAL_PROD_APEX}`,
    practice: `https://${APP_NAMES.PRACTICE}.${PORTAL_PROD_APEX}`,
    otel_collector_origin: PROD_OTEL_COLLECTOR,
  },
} as const;

export const ABSOLUTE_URL_REGEX = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;
export function isAbsoluteUrl(value: string) {
  return ABSOLUTE_URL_REGEX.test(value);
}

// Using [^\s@]+ (not \S+) to prevent ReDoS via overlapping segments containing '@'.
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isEmail(value: string) {
  return EMAIL_REGEX.test(value);
}

export function isSafeRedirect(value: string) {
  const isAbsolute = isAbsoluteUrl(value);
  if (!isAbsolute) {
    return true;
  }
  const url = new URL(value);
  return isLocalhost(url) || isLiftUrl(url);
}

export type HeaderGetter = {
  get(name: string): string | null;
};

export function getForwardedHeader(
  headers: HeaderGetter,
  name: string
): string | undefined {
  return headers.get(name)?.split(',')[0]?.trim() ?? undefined;
}

export function getEffectiveRequestUrl(params: {
  requestUrl: string;
  headers: HeaderGetter;
}): URL {
  const requestUrl = new URL(params.requestUrl);
  const forwardedHost = getForwardedHeader(params.headers, 'x-forwarded-host');
  const hostHeader = forwardedHost ?? params.headers.get('host');

  if (hostHeader) {
    requestUrl.host = hostHeader;
  }

  const forwardedProto = getForwardedHeader(
    params.headers,
    'x-forwarded-proto'
  );
  if (forwardedProto === 'http' || forwardedProto === 'https') {
    requestUrl.protocol = `${forwardedProto}:`;
  }

  const forwardedPort = getForwardedHeader(params.headers, 'x-forwarded-port');
  if (
    !requestUrl.port &&
    forwardedPort &&
    /^\d+$/.test(forwardedPort) &&
    forwardedPort !== '80' &&
    forwardedPort !== '443'
  ) {
    requestUrl.port = forwardedPort;
  }

  return requestUrl;
}

function getSafeHttpOrigin(
  value: string | null | undefined
): string | undefined {
  if (!value) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(value);
    if (parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:') {
      return parsedUrl.origin;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

/**
 * Apexes that may host an app taking part in the Auth0 flow.
 *
 * Deliberately narrower than `isLiftUrl`: Auth0 matches `redirect_uri` and
 * logout `returnTo` against a static per-application allow-list, so an origin
 * we accept but Auth0 does not simply breaks the flow with "Callback URL
 * mismatch". `workers.dev` and `pages.dev` are shared apexes that anyone can
 * take a subdomain on, so they must never qualify.
 */
export const AUTH_ORIGIN_APEXES = [
  PORTAL_PROD_APEX,
  PORTAL_STAGING_APEX,
  PORTAL_PREVIEW_APEX,
  PORTAL_DEV_APEX,
] as const;

/**
 * Whether `value` may be handed to Auth0 as a `redirect_uri` or logout
 * `returnTo`. Anything else has to be rejected rather than forwarded.
 */
export function isAllowedAuthOrigin(value: string | null | undefined): boolean {
  const origin = getSafeHttpOrigin(value);
  if (!origin) {
    return false;
  }

  const url = new URL(origin);
  if (isLocalhost(url)) {
    return true;
  }

  return (
    url.protocol === 'https:' &&
    AUTH_ORIGIN_APEXES.some(
      (apex) => url.hostname === apex || url.hostname.endsWith(`.${apex}`)
    )
  );
}

/**
 * The origin to use for Auth0 `redirect_uri` and logout `returnTo`.
 *
 * Taken solely from the URL Cloudflare routed the request on. `Referer`,
 * `Origin`, `X-Forwarded-*` and query/form parameters are all caller-controlled
 * and used to be reflected into these values, which let any page pick the
 * `redirect_uri` and made every Auth0-referred navigation (password reset,
 * logout) fail the callback check. None of them are consulted now.
 *
 * Request headers are read only to recover the browser-facing port when running
 * locally, where the dev server proxies to a worker that sees no port. Doing
 * that for a deployed host would put the `redirect_uri` back under the control
 * of whoever sent the request.
 */
export function resolveAuthOrigin(params: {
  requestUrl: string;
  headers: HeaderGetter;
}): string {
  const requestUrl = new URL(params.requestUrl);

  if (!isLocalhost(requestUrl) || requestUrl.port) {
    return requestUrl.origin;
  }

  const hostHeader = params.headers.get('host');
  const hostOrigin = getSafeHttpOrigin(
    hostHeader ? `${requestUrl.protocol}//${hostHeader}` : null
  );
  if (hostOrigin) {
    const hostUrl = new URL(hostOrigin);
    if (isLocalhost(hostUrl) && hostUrl.port) {
      return hostUrl.origin;
    }
  }

  const forwardedPort = getForwardedHeader(params.headers, 'x-forwarded-port');
  if (forwardedPort && /^\d+$/.test(forwardedPort)) {
    requestUrl.port = forwardedPort;
  }

  return requestUrl.origin;
}

export function buildLoginHref({
  pathname,
  searchParams,
  view,
}: {
  pathname: string;
  searchParams: URLSearchParams;
  view?: string;
}) {
  const nextParams = new URLSearchParams(searchParams);

  if (view) {
    nextParams.set('view', view);
  } else {
    nextParams.delete('view');
  }

  const nextSearch = nextParams.toString();
  return nextSearch ? `${pathname}?${nextSearch}` : pathname;
}

export function buildAuthRedirectHref(searchParams: URLSearchParams) {
  const nextParams = new URLSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  if (redirectTo) {
    nextParams.set('redirectTo', redirectTo);
  }

  const nextSearch = nextParams.toString();
  return nextSearch ? `/auth/redirect?${nextSearch}` : '/auth/redirect';
}

// #region User Roles

export const TFA_REQUIRED_ROLES = {
  LGH_SUPPORT_STAFF: 'LGH Support Staff',
  PROJECT_ADMIN: 'Project Admin',
} as const;

/**
 * Roles that can view Portal and manage users.
 */
export const ELEVATED_ROLE_NAMES = {
  ...TFA_REQUIRED_ROLES,
  GROUP_ADMIN: 'Group Admin',
  SITE_ADMIN: 'Site Admin',
} as const;

export const ROLE_NAMES = {
  ...ELEVATED_ROLE_NAMES,
  DATA_UPLOADER: 'Data Uploader',
  PARTICIPANT: 'Participant',
} as const;

export type RoleName = (typeof ROLE_NAMES)[keyof typeof ROLE_NAMES];

export const ROLE_RANKS = {
  [ROLE_NAMES.LGH_SUPPORT_STAFF]: 5,
  [ROLE_NAMES.PROJECT_ADMIN]: 4,
  [ROLE_NAMES.GROUP_ADMIN]: 3,
  [ROLE_NAMES.SITE_ADMIN]: 2,
  [ROLE_NAMES.DATA_UPLOADER]: 1,
  [ROLE_NAMES.PARTICIPANT]: 0,
} as const satisfies Record<RoleName, number>;

export type ElevatedRoleName =
  (typeof ELEVATED_ROLE_NAMES)[keyof typeof ELEVATED_ROLE_NAMES];
export type TfaRequiredRoleName =
  (typeof TFA_REQUIRED_ROLES)[keyof typeof TFA_REQUIRED_ROLES];
export type ParticipantRoleName = Extract<RoleName, 'Participant'>;

export function isRoleName(maybeRoleName: unknown): maybeRoleName is RoleName {
  return (
    typeof maybeRoleName === 'string' &&
    // @ts-expect-error - Object.values(ROLE_NAMES) is RoleName[] but maybeRoleName is only a string. But that's the point of this check, to see if maybeRoleName is a RoleName
    Object.values(ROLE_NAMES).includes(maybeRoleName)
  );
}

export function isTfaRequiredRole(
  maybeTfaRequiredRole: RoleName
): maybeTfaRequiredRole is TfaRequiredRoleName {
  return (
    // @ts-expect-error - Purpose of this function is to check the type
    Object.values(TFA_REQUIRED_ROLES).includes(maybeTfaRequiredRole)
  );
}

function isLivebornRequiredRoleToUpdate(targetRole: RoleName) {
  return (
    isElevatedRoleName(targetRole) || targetRole === ROLE_NAMES.DATA_UPLOADER
  );
}

export function isElevatedRoleName(
  maybeElevatedRoleName: RoleName
): maybeElevatedRoleName is ElevatedRoleName {
  return (
    // @ts-expect-error - Purpose of this function is to check the type
    Object.values(ELEVATED_ROLE_NAMES).includes(maybeElevatedRoleName)
  );
}

export function assertRoleName(
  maybeRoleName: unknown
): asserts maybeRoleName is RoleName {
  if (!isRoleName(maybeRoleName)) {
    throw new Error(`Expected RoleName, got ${typeof maybeRoleName}`);
  }
}

export function assertElevatedRoleName(
  maybeElevatedRoleName: RoleName
): asserts maybeElevatedRoleName is ElevatedRoleName {
  if (!isElevatedRoleName(maybeElevatedRoleName)) {
    throw new Error(
      `Expected ElevatedRoleName, got ${typeof maybeElevatedRoleName}`
    );
  }
}

export function canCreateTargetUser(
  loggedInUserRole: RoleName,
  targetRole: RoleName
) {
  return (
    isElevatedRoleName(loggedInUserRole) &&
    ROLE_RANKS[loggedInUserRole] > ROLE_RANKS[targetRole] &&
    // Liveborn rpc requires a TFA-verified auth token in order to create or edit elevated roles.
    (!isLivebornRequiredRoleToUpdate(targetRole) ||
      isTfaRequiredRole(loggedInUserRole))
  );
}

/**
 * The subset of an OrgDir organization-service entry that LIFT role resolution
 * needs. Declared structurally rather than importing the generated OrgDir DTOs,
 * so this package does not take a dependency on lift-portal-service.
 *
 * Compatible with `Dtos.V4.OrganizationServiceDto` (and its V3 equivalent), as
 * returned on `services` of a user record from `GET /api/v3/user/find`.
 */
export type OrgDirServiceMembership = {
  serviceId?: string | null;
  organizationId?: string | null;
  members?:
    | readonly ({ role?: { name?: string | null } | null } | null | undefined)[]
    | null;
};

/** One LIFT role a user holds, and the organization they hold it in. */
export type LiftRoleAssignment = {
  organizationId: string;
  role: RoleName;
};

export type ResolvedLiftRole = {
  /**
   * The user's effective role: the highest-ranked of `assignments`, or
   * `undefined` when the user holds no LIFT role in the requested scope.
   */
  role: RoleName | undefined;
  /**
   * Every matching assignment, in the order OrgDir returned them. More than one
   * distinct role here means the user's access is ambiguous and worth logging —
   * `role` resolves it by rank.
   */
  assignments: LiftRoleAssignment[];
};

function toNonEmptyStringSet(
  values: Iterable<string | null | undefined>
): Set<string> {
  const result = new Set<string>();
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      result.add(value);
    }
  }
  return result;
}

/**
 * Resolves the LIFT role a user holds within a given set of organizations.
 *
 * A user record from OrgDir carries a **flat** `services` array spanning every
 * organization and every Laerdal Connect service they belong to — other LIFT
 * projects, the web shop, everything. Neither the order of that array nor the
 * order of the memberships inside it is part of the API contract, so reading
 * the first entry yields a role from an arbitrary service in an arbitrary
 * organization. Both filters below are therefore load-bearing:
 *
 * - `liftServiceIds` keeps only LIFT Practice / LIFT Assessments. A service ID
 *   is a stable GUID per service across all organizations, which is why it
 *   alone cannot tell two projects apart.
 * - `visibleOrganizationIds` keeps only memberships inside the caller's current
 *   scope (typically `flatOrgs` for the selected project). An empty set matches
 *   nothing; callers that mean "everything" must pass the full scope
 *   explicitly.
 *
 * @see LIFT-2191
 */
export function resolveLiftRoleFromServices(params: {
  services:
    readonly (OrgDirServiceMembership | null | undefined)[] | null | undefined;
  liftServiceIds: Iterable<string | null | undefined>;
  visibleOrganizationIds: Iterable<string | null | undefined>;
}): ResolvedLiftRole {
  const { services, liftServiceIds, visibleOrganizationIds } = params;

  const allowedServiceIds = toNonEmptyStringSet(liftServiceIds);
  const allowedOrganizationIds = toNonEmptyStringSet(visibleOrganizationIds);

  const assignments: LiftRoleAssignment[] = [];

  for (const service of services ?? []) {
    const { serviceId, organizationId } = service ?? {};
    if (!serviceId || !allowedServiceIds.has(serviceId)) {
      continue;
    }
    if (!organizationId || !allowedOrganizationIds.has(organizationId)) {
      continue;
    }

    /*
     * `members` on a *user* record describes only that user's own membership of
     * this organization-service — OrgDir does not expand the organization's
     * other members here — so index 0 is the role we are after and there is no
     * need to match on `member.user.id`. A user can still hold several roles
     * overall, but they arrive as separate `services` entries (one per
     * organization-service), which is what the loop above walks.
     */
    const roleName = service?.members?.[0]?.role?.name;
    if (isRoleName(roleName)) {
      assignments.push({ organizationId, role: roleName });
    }
  }

  return { role: getHighestRankedRole(assignments), assignments };
}

/**
 * The most privileged of the given roles, or `undefined` when there are none.
 */
export function getHighestRankedRole(
  assignments: readonly { role: RoleName }[]
): RoleName | undefined {
  let highest: RoleName | undefined;
  for (const { role } of assignments) {
    if (highest === undefined || ROLE_RANKS[role] > ROLE_RANKS[highest]) {
      highest = role;
    }
  }
  return highest;
}
// #endregion User Roles

// #region User write failure reasons
/**
 * Reason codes surfaced by the user create/update service paths.
 *
 * Shared between `lift-portal-service` (which throws them) and `lift-portal`
 * (which maps them onto field-level form errors). Without a shared home the
 * portal would have to match on magic strings, or fall back to a generic
 * "Failed to create user" toast that tells the admin nothing.
 */
export const USER_WRITE_ERROR_CODES = {
  /** The email already belongs to an active LIFT user within the checked scope. */
  USER_ALREADY_EXISTS_IN_ORGANIZATIONS: 'USER_ALREADY_EXISTS_IN_ORGANIZATIONS',
  /** As above, reported by the Project Admin dev-tools path. */
  USER_ALREADY_EXISTS_IN_PROJECT: 'USER_ALREADY_EXISTS_IN_PROJECT',
  /** The assignment would exceed the span the role permits. */
  INVALID_ROLE_SPAN: 'INVALID_ROLE_SPAN',
  /**
   * An organization was submitted that is outside the acting admin's hierarchy,
   * or is not of the type the role's membership is expressed in.
   */
  INVALID_ORGANIZATION_ASSIGNMENT: 'INVALID_ORGANIZATION_ASSIGNMENT',
  /**
   * The email belongs to a LIFT user who already holds a *different* role. A
   * person holds exactly one LIFT role, so granting a second one is rejected even
   * where the uniqueness check itself is project-scoped and would allow the same
   * role to be granted in a further project.
   */
  USER_HOLDS_DIFFERENT_ROLE: 'USER_HOLDS_DIFFERENT_ROLE',
  /**
   * One or more selected sites have no Liveborn mapping in D1, so the access
   * cannot be granted there. Previously these were dropped from the request and
   * the user was provisioned with silently reduced access.
   */
  LIVEBORN_SITE_NOT_MAPPED: 'LIVEBORN_SITE_NOT_MAPPED',
  /**
   * One or more selected groups have no `Groups.simcapture_clientId`, so there
   * is no SimCapture client to make the admin an admin of.
   */
  SIMCAPTURE_GROUP_NOT_MAPPED: 'SIMCAPTURE_GROUP_NOT_MAPPED',
  /**
   * One or more selected sites have no `Sites.simcapture_organizationId`, so
   * the user cannot be attached to them in SimCapture.
   */
  SIMCAPTURE_SITE_NOT_MAPPED: 'SIMCAPTURE_SITE_NOT_MAPPED',
  /**
   * A SimCapture client-admin change could not be applied. The user's
   * SimCapture access no longer matches the portal.
   */
  SIMCAPTURE_CLIENT_UPDATE_FAILED: 'SIMCAPTURE_CLIENT_UPDATE_FAILED',
  /**
   * The acting admin is not a system admin of one of the SimCapture clients the
   * change would touch, so they may not grant or revoke admin rights in it.
   */
  SIMCAPTURE_CLIENT_UNAUTHORIZED: 'SIMCAPTURE_CLIENT_UNAUTHORIZED',
} as const;

export type UserWriteErrorCode =
  (typeof USER_WRITE_ERROR_CODES)[keyof typeof USER_WRITE_ERROR_CODES];
// #endregion User write failure reasons

// Helper functions
export function isLaerdalWorker(url: { hostname: string }) {
  return url.hostname.endsWith(
    `.${CLOUDFLARE_ACCOUNT_NAME}.${CLOUDFLARE_WORKERS_APEX}`
  );
}

export function isLiftUrl(maybeUrl: { hostname: string }): boolean {
  return LIFT_APEXES.some(
    (domain) =>
      maybeUrl.hostname === domain || maybeUrl.hostname.endsWith(`.${domain}`)
  );
}

export function isCloudflarePages(maybeUrl: { hostname: string }): boolean {
  return maybeUrl.hostname.endsWith(`.${CLOUDFLARE_PAGES_APEX}`);
}

export function isLocalhost(maybeUrl: { hostname: string }): boolean {
  return ['localhost', '127.0.0.1', ''].includes(maybeUrl.hostname);
}

// only works for our simple whatever.com apex domains
export function getApexHost(maybeUrl: { hostname: string }): string {
  return maybeUrl.hostname.split('.').slice(-2).join('.');
}

export function getRootHost(maybeUrl: { href: string }): string {
  const { hostname } = new URL(maybeUrl.href);
  if (isLiftUrl({ hostname })) {
    return getApexHost({ hostname });
  }
  return hostname;
}

export function getClientHost({
  subdomain,
  href,
}: {
  subdomain?: string;
  href: string;
}): string {
  const rootHost = getRootHost({ href });

  return isLiftUrl({ hostname: rootHost })
    ? `${subdomain}.${rootHost}`
    : new URL(href).host;
}

export function getClientOrigin({
  subdomain,
  href,
}: {
  subdomain?: string;
  href: string;
}): string {
  const host = getClientHost({ subdomain, href });
  const protocol = new URL(href).protocol;
  return `${protocol}//${host}`;
}

export function getOriginStackForPrefix(prefix: string): OriginStack {
  return {
    portal: `https://${getPreviewBranchHostname(prefix, APP_NAMES.PORTAL)}`,
    assessments: `https://${getPreviewBranchHostname(prefix, APP_NAMES.ASSESSMENTS)}`,
    practice: `https://${getPreviewBranchHostname(prefix, APP_NAMES.PRACTICE)}`,
    otel_collector_origin: DEV_OTEL_COLLECTOR,
  };
}

export function getPreviewOrigins(options: { hostname: string }) {
  if (options.hostname.endsWith('.' + PORTAL_PREVIEW_APEX)) {
    const [subdomain] = options.hostname.split('.');
    if (subdomain) {
      const appNameLastResults =
        APP_NAME_LAST_PREVIEW_SUBDOMAIN_REGEX.exec(subdomain);
      if (appNameLastResults?.groups?.prefix) {
        return getOriginStackForPrefix(appNameLastResults.groups.prefix);
      }
      const projectNameFirstResults =
        PROJECT_NAME_FIRST_PREVIEW_SUBDOMAIN_REGEX.exec(subdomain);
      if (projectNameFirstResults?.groups?.prefix) {
        return getOriginStackForPrefix(projectNameFirstResults.groups.prefix);
      }
    }
  }
  return ORIGIN_STACKS.preview;
}

export function getPreviewBranchHostname(branch: string, appName: AppName) {
  const projectName = CLOUDFLARE_PROJECT_NAMES[appName];

  if (isWorkersProjectName(projectName)) {
    const hostname = `${projectName}-${branch}.${PORTAL_PREVIEW_APEX}` as const;
    return hostname;
  }

  const hostname = `${branch}-${appName}.${PORTAL_PREVIEW_APEX}` as const;
  return hostname;
}

export function getOriginBranchHostname(branch: string, appName: AppName) {
  return `${branch}.${APP_ORIGIN_HOSTNAMES[appName]}`;
}

export function getOrigins(options: { hostname: string }): OriginStack {
  const deploymentEnvironment = getDeploymentEnvironment(options);
  if (deploymentEnvironment === 'preview') {
    return getPreviewOrigins(options);
  }

  return ORIGIN_STACKS[deploymentEnvironment];
}

export type ParsedUrl = {
  href: string;
  origin: string;
  protocol: string;
  username: string;
  password: string;
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
};

export function getParsedUrl(maybeUrl: URL | string): ParsedUrl {
  const url = typeof maybeUrl === 'string' ? new URL(maybeUrl) : maybeUrl;
  return {
    href: url.href,
    origin: url.origin,
    protocol: url.protocol,
    username: url.username,
    password: url.password,
    host: url.host,
    hostname: url.hostname,
    port: url.port,
    pathname: url.pathname,
    search: url.search,
    hash: url.hash,
  };
}

// Environment detection
export function getDeploymentEnvironment(maybeUrl: {
  hostname: string | undefined;
}): DeploymentEnvironment {
  const hostname = maybeUrl.hostname ?? '';
  if (isLocalhost({ hostname })) {
    return 'local';
  } else if (
    isCloudflarePages({ hostname }) ||
    hostname === PORTAL_PREVIEW_APEX ||
    hostname.endsWith(`.${PORTAL_PREVIEW_APEX}`)
  ) {
    return 'preview';
  } else if (
    hostname === PORTAL_PROD_APEX ||
    hostname.endsWith(`.${PORTAL_PROD_APEX}`)
  ) {
    return 'production';
  } else if (
    hostname === PORTAL_STAGING_APEX ||
    hostname.endsWith(`.${PORTAL_STAGING_APEX}`)
  ) {
    return 'staging';
  } else if (
    hostname === PORTAL_DEV_APEX ||
    hostname.endsWith(`.${PORTAL_DEV_APEX}`)
  ) {
    return 'dev';
  }

  console.warn(
    `Unrecognized hostname: ${hostname}. Defaulting to 'local' environment.`
  );
  return 'local';
}

export function createLogger(
  oneOrMoreCategories: string | string[],
  options: LoggerOptions = {}
) {
  const defaults: LoggerOptions = {
    minLevel: 'debug',
    format: (entry: LogEntry) => {
      const hasWindow = 'window' in globalThis;
      const formatter = !hasWindow // on a "server"
        ? formatters['json-pretty']
        : formatters['text'];
      return formatter(entry);
    },
  };

  return new Logger(oneOrMoreCategories, { ...defaults, ...options });
}

export function withErrorLogging<T>(
  logger: Logger,
  fn: () => Promise<T>
): Promise<T> {
  return fn().catch((err) => {
    logger.error('Unhandled RPC error', {
      message: err?.message,
      name: err?.name,
      stack: err?.stack,
    });
    throw err;
  });
}

export function getUsernamePrefix(firstName: string) {
  return firstName.replaceAll(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

/**
 * Safely extracts and trims a string value from FormData.
 * Returns null if the key doesn't exist or the value is not a string.
 *
 * @param formData - The FormData object to extract from
 * @param key - The key to look up in the FormData
 * @returns The trimmed string value, or null if not found or not a string
 *
 * @example
 * ```typescript
 * const formData = await request.formData();
 * const email = getFormString(formData, 'email'); // string | null
 * const name = getFormString(formData, 'name') ?? 'Unknown'; // with default
 * ```
 */
export function getFormString(formData: FormData, key: string): string | null {
  const value = formData.get(key);
  if (value === null || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Extracts and trims a required string value from FormData.
 * Throws an error if the key doesn't exist, the value is not a string, or is empty (including whitespace-only strings after trimming).
 *
 * @param formData - The FormData object to extract from
 * @param key - The key to look up in the FormData
 * @param message - Optional custom error message. Defaults to "{key} is required"
 * @returns The trimmed string value (guaranteed to be non-empty)
 * @throws Error if the value is missing, not a string, or empty
 *
 * @example
 * ```typescript
 * const formData = await request.formData();
 * const email = getRequiredFormString(formData, 'email'); // string (throws if missing)
 * const userId = getRequiredFormString(formData, 'userId'); // throws "{key} is required"
 * const name = getRequiredFormString(formData, 'name', 'Name cannot be empty'); // custom message
 * ```
 */
export function getRequiredFormString(
  formData: FormData,
  key: string,
  message?: string
): string {
  const value = getFormString(formData, key);
  if (!value) {
    throw new Error(message ?? `${key} is required`);
  }
  return value;
}

/**
 * Extracts all string values for a key from FormData, trims them, and filters out empties.
 *
 * @param formData - The FormData object to extract from
 * @param key - The key to look up in the FormData
 * @returns An array of trimmed, non-empty string values
 *
 * @example
 * ```typescript
 * const formData = await request.formData();
 * const groupIds = getFormDataStringArray(formData, 'assigned-group');
 * ```
 */
export function getFormDataStringArray(
  formData: FormData,
  key: string
): string[] {
  return formData
    .getAll(key)
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}
